import { useState } from 'react';
import { Phone, Heart, ChevronDown, HelpCircle, Calendar, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { ServicesGrid } from './ServicesGrid';
import { LeaderSection } from './LeaderSection';
import { TestimonialList } from './TestimonialList';
import { FAQSection } from './FAQSection';
import { ContactSection } from './ContactSection';
import { CallModal, isTouchDevice } from './CallModal';

export const Home = () => {
  const [callModalOpen, setCallModalOpen] = useState(false);

  const handleCallClick = (e) => {
    if (!isTouchDevice()) {
      e.preventDefault();
      setCallModalOpen(true);
    }
    if (window.gtag_report_conversion) {
      window.gtag_report_conversion(`tel:${mockData.company.phoneTel}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <CallModal isOpen={callModalOpen} onClose={() => setCallModalOpen(false)} />
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/24wkpkvm_logo.png"
                alt="TruBridge Health Solutions Logo"
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{mockData.company.name}</h1>
                <p className="text-xs text-gray-600">{mockData.company.tagline}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors">Services</a>
              <a href="#agents" className="text-gray-700 hover:text-blue-600 transition-colors">Our Team</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors">Reviews</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 transition-colors">FAQ</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors">Contact</a>
              <a href="/medicare" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors" data-testid="nav-medicare-link">Medicare Help</a>
            </nav>
            <div className="hidden md:flex items-center space-x-2 bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-medium">
              <Phone className="h-4 w-4" />
              <span>{mockData.company.phone}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/fe9sb6vu_shutterstock_1984125173.jpg"
            alt="Healthcare consultation"
            className="w-full h-full object-cover object-[center_65%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/50 to-blue-50/40"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              Trusted Medicare & Health Insurance Experts in Westchester County, NY
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Navigate Medicare with
              <span className="text-blue-600"> Confidence</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Serving Westchester County and the greater New York Tri-State area with personalized guidance from licensed insurance professionals who care about your health and financial wellbeing. Free consultations available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all hover:scale-105">
                  Get Your Free Quote
                  <ChevronDown className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href={`tel:${mockData.company.phoneTel}`} onClick={handleCallClick}>
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-50 border-2 border-blue-600 px-8 py-6 text-lg font-semibold transition-all hover:scale-105">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Free 15-Minute Medicare Q&A - Compact CTA */}
      <section className="py-10 md:py-14 bg-gradient-to-r from-blue-50 to-indigo-50 border-y border-blue-100" data-testid="medicare-qa-top">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 md:gap-0">
            {/* Left - Icon */}
            <div className="flex-shrink-0">
              <div className="w-28 h-28 md:w-32 md:h-32 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <HelpCircle className="h-14 w-14 md:h-16 md:w-16 text-white" />
              </div>
            </div>

            {/* Center - Copy */}
            <div className="flex-1 text-center px-6 md:px-16">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Have a Medicare Question?</h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                Get clear answers in a free 15-minute Medicare Q&A. No pressure. No obligation. Just answers to help you understand your options.
              </p>
            </div>

            {/* Right - Button */}
            <div className="flex-shrink-0">
              <a href={process.env.REACT_APP_CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="medicare-qa-top-btn">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-10 py-8 text-lg sm:text-xl transition-all hover:scale-105 whitespace-nowrap shadow-lg">
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule Your Free 15-Minute Q&A
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Empowering Healthcare Journey Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">Empowering Your Healthcare Journey</h3>
            <p className="text-lg md:text-xl leading-relaxed">
              TruBridge Health specializes in guiding individuals throughout Westchester County and the greater New York Tri-State area through the complexities of Medicare. Our dedicated team offers personalized support to help you understand your options and make informed decisions about your healthcare. Whether you're enrolling for the first time or navigating changes, we provide valuable resources and expert advice tailored to your unique needs. Trust us to be your local ally in achieving optimal health and wellness through Medicare.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-65">
          <img
            src="https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/ke5bdmp0_shutterstock_2247626273.jpg"
            alt="Healthcare technology"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="h-16 w-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h3>
            <p className="text-xl text-gray-900 font-semibold leading-relaxed bg-white/80 py-4 px-6 rounded-lg">{mockData.company.mission}</p>
          </div>
        </div>
      </section>

      {/* Trusted Insurance Partners Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Trusted Insurance Partners</h3>
            <p className="text-blue-100 mb-10">We work with leading insurance carriers to provide you with the best coverage options</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {[
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/k8q49yaq_Aetna_logo_reg_rgb_vio.png', alt: 'Aetna' },
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/fdmcaedu_176995e2713af3efd424791f7bca01d8.png', alt: 'UnitedHealthcare' },
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/z4v4ig8n_d5526f61ac785a1d0508d59520287501.png', alt: 'Humana' },
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/x04crccq_98f63d63e2aef75614966e98fef8d9b8.png', alt: 'WellCare' },
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/db6gy3y0_Screenshot%202026-01-26%20at%204.45.16%E2%80%AFPM.png', alt: 'Globe Life' },
                { src: 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/hx0jsnhv_d706748f933b6d51fe12d34786d1a405.png', alt: 'MVP' },
              ].map((logo) => (
                <div key={logo.alt} className="bg-white rounded-lg p-4 h-20 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow" style={{minWidth: '140px'}}>
                  <img src={logo.src} alt={logo.alt} className="max-h-12 w-auto object-contain" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ServicesGrid />
      <LeaderSection />
      <TestimonialList />
      <FAQSection />
      <ContactSection onCallClick={handleCallClick} />

      {/* Bottom Medicare CTA */}
      <section className="py-12 md:py-16 bg-slate-900" data-testid="medicare-qa-bottom">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
            <img
              src="https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/ytwcldm5_BFB_0045.jpg"
              alt="Adeen Schwartz"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shadow-lg flex-shrink-0"
            />
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Not Sure Where to Start with Medicare?</h3>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Schedule a complimentary 15-minute Q&A and get answers to your Medicare questions.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a href={process.env.REACT_APP_CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="medicare-qa-bottom-btn">
                <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-6 py-5 text-sm sm:text-base transition-all hover:scale-105 whitespace-nowrap">
                  <Calendar className="mr-2 h-4 w-4" />
                  Free 15-Minute Q&A
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/24wkpkvm_logo.png"
                  alt="TruBridge Health Solutions Logo"
                  className="h-12 w-auto"
                />
                <h4 className="text-xl font-bold">{mockData.company.name}</h4>
              </div>
              <p className="text-gray-400 mb-4">{mockData.company.tagline}</p>
              <div className="text-xs text-gray-400 leading-relaxed border-t border-gray-800 pt-4">
                <p className="mb-2">
                  <strong>Important:</strong> We do not offer every plan available in your area. Any information we provide is limited to those plans we do offer in your area. Please contact Medicare.gov or 1-800-MEDICARE to get information on all of your options.
                </p>
                <p className="mb-2">
                  TruBridge Health is not connected with or endorsed by the U.S. government or the federal Medicare program.
                </p>
                <p>
                  We are committed to protecting your privacy. All personal information submitted through this website is kept confidential and used solely for the purpose of providing you with insurance information and services.
                </p>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#agents" className="hover:text-white transition-colors">Our Team</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Reviews</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Contact</h5>
              <ul className="space-y-2 text-gray-400">
                <li>{mockData.company.phone}</li>
                <li>{mockData.company.email}</li>
                <li>{mockData.company.address}</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-800">
                <p className="font-semibold text-white">Adeen Schwartz</p>
                <p className="text-gray-400 text-sm">Licensed Health and Life Agent</p>
                <p className="text-gray-400 text-sm">NPN# 21606228</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} {mockData.company.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
